import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Paper,
  Divider,
  LinearProgress,
  Alert,
  AlertTitle,
  Stack,
  Tabs,
  Tab,
  Badge,
  TextField,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  RateReview as RateReviewIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  Flag as FlagIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { userState } from 'redux/userSlice';
import { organizationState } from 'redux/organizationSlice';
import { certificatesState, clientActions } from 'redux/clientSlice';
import { generateClient } from 'aws-amplify/api';
import { clientVendorsByClient } from 'graphql/vendor';
import { getNotificationsByUser } from 'graphql/notifications';
import StatusIndicator from 'components/shared/StatusIndicator';
import { useNavigate } from 'react-router-dom';
import { getNavigationPath, useNavPath, NAVIGATED_FROM } from 'constants/routes';
import FlaggedItems from './FlaggedItems';
import CommentIcon from '@mui/icons-material/Comment';
import ClientActivityDashboard from './ClientActivityDashboard';

const API = generateClient();

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`activity-tabpanel-${index}`}
      aria-labelledby={`activity-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Risk level thresholds in dollars
const RISK_THRESHOLDS = {
  HIGH: 500000,
  MEDIUM: 100000,
  LOW: 25000
};

const getRiskLevel = (exposure) => {
  if (!exposure || exposure === 0) return { level: 'N/A', color: 'rgba(0, 0, 0, 0.08)', textColor: 'rgba(0, 0, 0, 0.6)' };
  if (exposure > RISK_THRESHOLDS.HIGH) return { level: 'HIGH', color: 'rgba(139, 0, 0, 0.1)', textColor: 'darkred' };
  if (exposure > RISK_THRESHOLDS.MEDIUM) return { level: 'MEDIUM', color: 'rgba(225, 165, 0, 0.1)', textColor: 'chocolate' };
  if (exposure > RISK_THRESHOLDS.LOW) return { level: 'LOW', color: 'rgba(0, 128, 0, 0.1)', textColor: 'green' };
  return { level: 'MINIMAL', color: 'rgba(128, 0, 128, 0.1)', textColor: 'purple' };
};

const getComplianceColor = (percentage) => {
  if (percentage >= 90) return { color: 'rgba(0, 128, 0, 0.1)', barColor: 'green' };
  if (percentage >= 70) return { color: 'rgba(225, 165, 0, 0.1)', barColor: 'chocolate' };
  return { color: 'rgba(139, 0, 0, 0.1)', barColor: 'darkred' };
};

const calculateCompliance = (certificate) => {
  // Calculate compliance based on coverage requirements met and validation errors
  let score = 100;
  const validationErrors = certificate.validationErrors || [];
  const coveragesMet = certificate.coveragesMet || 0;
  const totalCoverages = certificate.totalCoverages || 1;
  
  // Deduct points for validation errors
  score -= validationErrors.length * 10;
  
  // Calculate coverage compliance
  const coverageCompliance = (coveragesMet / totalCoverages) * 100;
  
  // Weight the final score
  const finalScore = (score * 0.4) + (coverageCompliance * 0.6);
  
  return Math.max(0, Math.min(100, Math.round(finalScore)));
};

// Policy type mapping
const POLICY_TYPES = {
  GL: 'General Liability',
  AU: 'Auto Liability',
  UMB: 'Umbrella/Excess',
  WC: 'Workers Compensation'
};

export default function MyActivity() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedInUser } = useSelector(userState);
  const { selectedClient, userOrgType } = useSelector(organizationState);
  const { certificates } = useSelector(certificatesState);
  const NAV_PATH = useNavPath(userOrgType);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [reviewSearchTerm, setReviewSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activityData, setActivityData] = useState({
    recentActivity: [],
    upcomingExpirations: [],
    reviewItems: [],
    recentComments: [],
    stats: {
      totalItems: 0,
      expiringCount: 0,
      reviewCount: 0,
      highRiskCount: 0,
      commentMentions: 0
    }
  });

  const handleCertificateClick = (certificateId, vendorId) => {
    const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
      clientId: selectedClient.id,
      vendorId: vendorId || NAVIGATED_FROM.CERTIFICATES,
      certificateId
    });
    navigate(certificatePath);
  };

  const toggleExpanded = (certificateId) => {
    setExpandedItems(prev => ({
      ...prev,
      [certificateId]: !prev[certificateId]
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const fetchUserActivity = useCallback(async () => {
    setLoading(true);
    try {
      // First fetch vendors for this client
      let vendorMap = {};
      if (selectedClient?.clientID) {
        const vendorResponse = await API.graphql({
          query: clientVendorsByClient,
          variables: { clientID: selectedClient.clientID }
        });
        const vendorItems = vendorResponse?.data?.clientVendorsByClient?.items || [];
        vendorItems.forEach(item => {
          if (item.vendor) {
            vendorMap[item.vendor.id] = item.vendor;
          }
        });
        setVendors(vendorMap);
      }

      // Fetch recent comment mentions for the logged in user
      let recentCommentMentions = [];
      if (loggedInUser?.id) {
        try {
          const notificationsResponse = await API.graphql({
            query: getNotificationsByUser,
            variables: {
              userID: loggedInUser.id,
              filter: { type: { eq: 'COMMENT_MENTION' } },
              sortDirection: 'DESC',
              limit: 10
            }
          });
          recentCommentMentions = notificationsResponse?.data?.notificationsByUser?.items || [];
        } catch (error) {
          console.error('Error fetching comment mentions:', error);
        }
      }

      // Use certificates from Redux store which already has all certificates
      const allCertificates = certificates || [];
      
      // Filter certificates by user activity
      const userCertificates = allCertificates.filter(cert => {
        // Check if user has worked on this certificate
        return cert.updatedAt || cert.createdAt; // All certs for now, can add user filtering later
      });

      // Filter certificates in review status
      const reviewCertificates = allCertificates.filter(cert => cert.status === 'REVIEW');

      // Process upcoming expirations (within 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      
      const upcomingExpirations = userCertificates.filter(cert => {
        if (!cert.firstExp) return false;
        const expDate = new Date(cert.firstExp);
        return expDate <= thirtyDaysFromNow && expDate >= new Date();
      }).sort((a, b) => new Date(a.firstExp) - new Date(b.firstExp));

      // Calculate risk exposure for each certificate
      const processedReviewItems = reviewCertificates.map(cert => {
        let totalExposure = 0;
        const exposureBreakdown = [];
        
        // Generate exposure breakdown by policy type (placeholder data - would come from actual coverage analysis)
        const policyTypes = ['GL', 'AU', 'UMB', 'WC'];
        policyTypes.forEach(type => {
          const gap = Math.random() * 250000; // Placeholder - would calculate actual gap
          if (gap > 0) {
            exposureBreakdown.push({
              type,
              name: POLICY_TYPES[type],
              exposureGap: gap
            });
            totalExposure += gap;
          }
        });

        const compliance = calculateCompliance(cert);
        const risk = getRiskLevel(totalExposure);

        // Add vendor info with fallback to insured name
        const vendorInfo = vendorMap[cert.clientVendorID] || {};
        // If no vendor found, create one from certificate insured info
        if (!vendorInfo.name && !vendorInfo.entityName && (cert.insuredName || cert.insured)) {
          vendorInfo.name = cert.insuredName || cert.insured;
        }

        return {
          ...cert,
          vendor: vendorInfo,
          totalExposure,
          exposureBreakdown,
          compliancePercentage: compliance,
          riskLevel: risk.level,
          riskColor: risk.color,
          riskTextColor: risk.textColor
        };
      });

      // Calculate stats
      const highRiskCount = processedReviewItems.filter(item => item.riskLevel === 'HIGH').length;

      // Process comment mentions to create activity items
      const commentActivityItems = recentCommentMentions.map(notification => ({
        id: `comment-${notification.id}`,
        type: 'comment',
        certificateID: notification.certificateID,
        clientVendorID: notification.vendorID,
        clientID: notification.clientID,
        title: notification.title,
        message: notification.message,
        metadata: notification.metadata ? JSON.parse(notification.metadata) : {},
        createdAt: notification.createdAt,
        updatedAt: notification.createdAt,
        status: 'COMMENT_MENTION'
      }));

      // Add vendor info to all certificates - include insured info as fallback
      const recentCertificates = userCertificates.slice(0, 10).map(cert => {
        const vendor = vendorMap[cert.clientVendorID] || {};
        // If no vendor found, create one from certificate insured info
        if (!vendor.name && !vendor.entityName && (cert.insuredName || cert.insured)) {
          vendor.name = cert.insuredName || cert.insured;
        }
        return {
          ...cert,
          type: 'certificate',
          vendor
        };
      });

      // Combine certificates and comment mentions, sort by most recent
      const combinedRecentActivity = [...recentCertificates, ...commentActivityItems]
        .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
        .slice(0, 10);

      const expirationsWithVendors = upcomingExpirations.map(cert => {
        const vendor = vendorMap[cert.clientVendorID] || {};
        // If no vendor found, create one from certificate insured info
        if (!vendor.name && !vendor.entityName && (cert.insuredName || cert.insured)) {
          vendor.name = cert.insuredName || cert.insured;
        }
        return {
          ...cert,
          vendor
        };
      });

      setActivityData({
        recentActivity: combinedRecentActivity,
        upcomingExpirations: expirationsWithVendors,
        reviewItems: processedReviewItems,
        recentComments: commentActivityItems,
        stats: {
          totalItems: userCertificates.length,
          expiringCount: upcomingExpirations.length,
          reviewCount: processedReviewItems.length,
          highRiskCount,
          commentMentions: commentActivityItems.length
        }
      });
    } catch (error) {
      console.error('Error fetching user activity:', error);
    } finally {
      setLoading(false);
    }
  }, [certificates, selectedClient, loggedInUser]);

  useEffect(() => {
    if (selectedClient?.clientID) {
      // Fetch all certificates if not already loaded
      if (!certificates) {
        dispatch(clientActions.getAllClientCertificates(selectedClient.clientID));
      }
    }
  }, [selectedClient, dispatch, certificates]);

  useEffect(() => {
    if (certificates && selectedClient?.clientID) {
      fetchUserActivity();
    }
  }, [certificates, selectedClient, fetchUserActivity]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Welcome {loggedInUser?.firstName || 'User'}
        </Typography>
        <Typography variant="h5" gutterBottom sx={{ mt: 1 }}>
          Activity
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Stay up to date with your work items and certificates requiring attention
        </Typography>
      </Box>

      {/* Client-level Activity Dashboard */}
      <ClientActivityDashboard
        selectedClient={selectedClient}
        vendorLabel={selectedClient?.vendorLabel || 'Subcontractor'}
      />

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="activity tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<Badge badgeContent={activityData.reviewItems.length} color="primary">
              <RateReviewIcon />
            </Badge>}
            label="Items in Review"
            id="activity-tab-0"
            aria-controls="activity-tabpanel-0"
          />
          <Tab 
            icon={<FlagIcon />}
            label="Flagged Items"
            id="activity-tab-1"
            aria-controls="activity-tabpanel-1"
          />
          <Tab 
            icon={<Badge badgeContent={activityData.recentActivity.length} color="primary">
              <HistoryIcon />
            </Badge>}
            label="Recent Activity"
            id="activity-tab-2"
            aria-controls="activity-tabpanel-2"
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {/* Review Items with Compliance */}
        <Paper elevation={1} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <RateReviewIcon sx={{ mr: 1 }} />
                Items in Review
              </Typography>
              <TextField
                size="small"
                placeholder="Search certificates..."
                value={reviewSearchTerm}
                onChange={(e) => setReviewSearchTerm(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: reviewSearchTerm && (
                    <InputAdornment position="end">
                      <ClearIcon 
                        fontSize="small" 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setReviewSearchTerm('')}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />
            {activityData.reviewItems.length > 0 ? (
              <>
                {(() => {
                  const filteredItems = activityData.reviewItems
                    .filter(cert => {
                    if (!reviewSearchTerm) return true;
                    
                    const searchLower = reviewSearchTerm.toLowerCase();
                    
                    // Search across all relevant certificate fields
                    const searchableFields = [
                      cert.name,
                      cert.id,
                      cert.status,
                      cert.insuredName,
                      cert.insured,
                      cert.producer,
                      cert.certificateHolder,
                      cert.description,
                      cert.projectID,
                      cert.firstExp,
                      cert.certificateDate,
                      cert.vendor?.name,
                      cert.vendor?.entityName,
                      cert.vendor?.email,
                      cert.vendor?.phone,
                      cert.vendor?.address,
                      cert.vendor?.contactName,
                      cert.riskLevel
                    ];
                    
                    // Safely add coverage information if it's an array
                    if (Array.isArray(cert.coverages)) {
                      searchableFields.push(
                        ...cert.coverages.map(c => c?.type).filter(Boolean),
                        ...cert.coverages.map(c => c?.carrier).filter(Boolean)
                      );
                    }
                    
                    // Safely add validation errors if it's an array
                    if (Array.isArray(cert.validationErrors)) {
                      searchableFields.push(...cert.validationErrors);
                    }
                    
                    // Safely add exposure breakdown info if it's an array
                    if (Array.isArray(cert.exposureBreakdown)) {
                      searchableFields.push(
                        ...cert.exposureBreakdown.map(e => e?.name).filter(Boolean),
                        ...cert.exposureBreakdown.map(e => e?.type).filter(Boolean)
                      );
                    }
                    
                    // Check if any field contains the search term
                    return searchableFields.some(field => 
                      field && String(field).toLowerCase().includes(searchLower)
                    );
                  });

                  return (
                    <>
                      {reviewSearchTerm && (
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing {filteredItems.length} of {activityData.reviewItems.length} certificates
                          </Typography>
                        </Box>
                      )}
                      {filteredItems.length > 0 ? (
                        <>
                        <List>
                          {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cert, index) => (
                  <Box key={cert.id}>
                    <ListItem
                      divider={index < Math.min(rowsPerPage, filteredItems.length - page * rowsPerPage) - 1}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                        py: 1.5
                      }}
                    >
                      <ListItemAvatar onClick={() => handleCertificateClick(cert.id, cert.clientVendorID)}>
                        <Avatar sx={{ bgcolor: cert.riskColor, width: 36, height: 36 }}>
                          <TrendingUpIcon sx={{ color: cert.riskTextColor, fontSize: 20 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        onClick={() => handleCertificateClick(cert.id, cert.clientVendorID)}
                        primary={
                          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                            <Box display="flex" alignItems="center" gap={1} flex="1 1 auto">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {cert.name || `Certificate ${cert.id.slice(-6)}`}
                              </Typography>
                              <OpenInNewIcon sx={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.4)' }} />
                              <Chip
                                label={`${cert.riskLevel} RISK`}
                                size="small"
                                sx={{
                                  backgroundColor: cert.riskColor,
                                  color: cert.riskTextColor,
                                  fontWeight: 500,
                                  borderRadius: '5px',
                                  height: 22,
                                  fontSize: '0.7rem'
                                }}
                              />
                            </Box>
                            <Box display="flex" alignItems="center" gap={3} flex="0 0 auto">
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {cert.vendor?.name || cert.vendor?.entityName || cert.insuredName || cert.insured || 'Vendor'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {formatDistanceToNow(new Date(cert.createdAt), { addSuffix: true })}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 120 }}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {cert.compliancePercentage}%
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={cert.compliancePercentage}
                                  sx={{
                                    width: 60,
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: getComplianceColor(cert.compliancePercentage).barColor
                                    }
                                  }}
                                />
                              </Box>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpanded(cert.id);
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  ${cert.totalExposure.toLocaleString()}
                                </Typography>
                                {expandedItems[cert.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </Box>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {expandedItems[cert.id] && cert.exposureBreakdown && (
                      <Box sx={{ pl: 9, pr: 2, pb: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                        <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>
                          Exposure Gap Breakdown:
                        </Typography>
                        {cert.exposureBreakdown.map((item, idx) => (
                          <Box key={idx} display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {item.type} - {item.name}:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ${item.exposureGap.toLocaleString()}
                            </Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" fontWeight="500">
                            Total Exposure Gap:
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            ${cert.totalExposure.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  ))}
                        </List>

                        {/* Pagination Controls */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Rows per page:
                            </Typography>
                            <FormControl size="small">
                              <Select
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                sx={{ minWidth: 70 }}
                              >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>

                          <TablePagination
                            component="div"
                            count={filteredItems.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[]}
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                            sx={{
                              border: 'none',
                              '.MuiTablePagination-toolbar': {
                                paddingLeft: 0,
                                paddingRight: 0
                              },
                              '.MuiTablePagination-selectLabel, .MuiTablePagination-select': {
                                display: 'none'
                              }
                            }}
                          />
                        </Box>
                        </>
                      ) : (
                        <Alert severity="info">
                          <AlertTitle>No matching certificates</AlertTitle>
                          No certificates found matching "{reviewSearchTerm}". Try adjusting your search terms.
                        </Alert>
                      )}
                    </>
                  );
                })()}
              </>
            ) : (
              <Alert severity="info">
                <AlertTitle>No Items in Review</AlertTitle>
                There are no certificates currently in review status.
              </Alert>
            )}
          </Paper>
      </TabPanel>

      {/* Flagged Items Tab */}
      <TabPanel value={activeTab} index={1}>
        <FlaggedItems />
      </TabPanel>

      {/* Recent Activity Tab */}
      <TabPanel value={activeTab} index={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ mr: 1 }} />
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {activityData.recentActivity.length > 0 ? (
              <List>
                {activityData.recentActivity.map((item, index) => {
                  const isComment = item.type === 'comment';
                  return (
                    <ListItem 
                      key={item.id} 
                      divider={index < activityData.recentActivity.length - 1}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                      onClick={() => {
                        if (isComment && item.certificateID) {
                          handleCertificateClick(item.certificateID, item.clientVendorID);
                        } else if (!isComment) {
                          handleCertificateClick(item.id, item.clientVendorID);
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: isComment ? 'rgba(33, 150, 243, 0.1)' : 'rgba(0, 0, 0, 0.08)' }}>
                          {isComment ? (
                            <CommentIcon sx={{ color: '#2196f3' }} />
                          ) : (
                            <AssignmentIcon sx={{ color: 'rgba(0, 0, 0, 0.6)' }} />
                          )}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {isComment ? (
                              <>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {item.title || 'Comment Mention'}
                                </Typography>
                                <Chip
                                  label="Mention"
                                  size="small"
                                  color="primary"
                                  sx={{ height: 20 }}
                                />
                              </>
                            ) : (
                              <>
                                {item.name || `Certificate ${item.id.slice(-6)}`}
                                <OpenInNewIcon sx={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.4)' }} />
                              </>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {isComment ? (
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  {item.message}
                                </Typography>
                                {item.metadata?.comment && (
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                      fontStyle: 'italic',
                                      mt: 0.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '500px'
                                    }}
                                  >
                                    "{item.metadata.comment}"
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                                </Typography>
                              </>
                            ) : (
                              <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="body2" color="text.secondary">
                                  {item.vendor?.name || item.vendor?.entityName || item.vendor?.useEntityName && item.vendor?.entityName || item.insuredName || item.insured || 'Vendor'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {selectedClient?.name || 'Client'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Last updated: {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                      {!isComment && (
                        <StatusIndicator
                          label={item.status}
                          size="small"
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Alert severity="info">
                <AlertTitle>No Recent Activity</AlertTitle>
                You haven't worked on any certificates recently.
              </Alert>
            )}
        </Paper>
      </TabPanel>
    </Box>
  );
}